import { cx } from '@libs/classnames';
import { pluralize, PluralWordForms } from '@libs/i18n';

import {
  ClockControlSettings,
  ClockControlType,
  OneOnOneSettings,
  SwissSettings,
  TournamentMode,
  TournamentSystem,
} from '@features/shared/api/typings';
import { TIME_CONTROL } from '@features/shared/constants';

import styles from './TournamentSystemView.module.css';

const systems: { [k in TournamentSystem]: string } = {
  RoundRobin: 'Круговая',
  Swiss: 'Швейцарская',
  Knockout: 'Нокаут',
  OneOnOne: 'Матч',
  Seance: 'Одновременная игра',
};

const gamesPlural: PluralWordForms = {
  one: '{number}\u00A0партия',
  few: '{number}\u00A0партии',
  many: '{number}\u00A0партий',
};

const roundsPlural: PluralWordForms = {
  one: '{number}\u00A0тур',
  few: '{number}\u00A0тура',
  many: '{number}\u00A0туров',
};

export type Props = {
  className?: string;
  tournament?: {
    system: TournamentSystem;
    clockControl?: ClockControlType;
    mode?: TournamentMode;
    clockControlSettingsPlayer1?: ClockControlSettings;
    clockControlSettingsPlayer2?: ClockControlSettings;
    oneOnOneSettings?: OneOnOneSettings;
    swissSettings?: SwissSettings;
    teamsCount?: number | null;
    participantsCount?: number;
    participantsCountMax?: number | null;
  };
  showParticipants?: boolean;
};

export function TournamentSystemView({ className, tournament, showParticipants }: Props) {
  if (!tournament) return null;
  const isMatch = tournament.system === 'OneOnOne' || tournament.system === 'Seance';
  const player1 = tournament.clockControlSettingsPlayer1;
  const player2 = tournament.clockControlSettingsPlayer2;

  let system = systems[tournament.system];

  let time = '';
  if (tournament.clockControl) {
    if (!isMatch) {
      time = TIME_CONTROL[tournament.clockControl] + ',\u00A0';
    }

    if (player1?.initialSeconds !== undefined && player1?.addSecondsPerMove !== undefined) {
      time += `${Math.floor(player1.initialSeconds / 60)}'\u00A0+\u00A0${player1.addSecondsPerMove}"`;
    }

    if (
      isMatch &&
      tournament.mode === 'Private' &&
      player2 &&
      (player1?.initialSeconds !== player2.initialSeconds ||
        player1?.addSecondsPerMove !== player2?.addSecondsPerMove)
    ) {
      time += `, ${Math.floor(player2.initialSeconds / 60)}'\u00A0+\u00A0${player2.addSecondsPerMove}"`;
    }
  }

  let gamesCount;
  if (isMatch && tournament.oneOnOneSettings) {
    gamesCount = pluralize(gamesPlural, tournament.oneOnOneSettings.gamesCount);
  }
  if (tournament.system === 'Swiss' && tournament.swissSettings?.roundsCount) {
    gamesCount = pluralize(roundsPlural, tournament.swissSettings?.roundsCount);
  }

  // Дальше немного джедайское:
  // 1. Нам нужно, чтобы количество участников всегда было выравнено по правому краю.
  // 2. Количество участников всегда должно быть не одно на строке, иначе будет дырка.
  // То есть всегда есть левый сосед у participantsCount.
  // 3. gamesCount и time могут не существовать
  // 4. Данные должны естественным образом переноситься.
  // Например, если там только система, она короткая, то у нас будет одна строка.
  // 5. Также у нас должны быть символы ·, и они должны оставаться на верхней строке.
  // Эти символы добавляются только если есть содержимое СЛЕДУЮЩЕГО блока.

  const systemSeparator = gamesCount || time ? '\u00A0· ' : '';
  system = system + systemSeparator;
  const gamesCountSeparator = gamesCount && time ? '\u00A0· ' : '';
  gamesCount = gamesCount ? gamesCount + gamesCountSeparator : undefined;

  const existingData = [system];
  if (gamesCount) existingData.push(gamesCount);
  if (time) existingData.push(time);

  const lastRowInfo = existingData.pop();

  return (
    <span className={cx(className, styles.root)}>
      {existingData.map((item) => (
        <span key={item}>{item}</span>
      ))}
      <div className={styles.lastRow}>
        {lastRowInfo}
        {showParticipants && (
          <span className={styles.participantsCount}>
            {tournament.mode === 'Team' ||
            tournament.mode === 'PrivateTeam' ||
            tournament.mode === 'TwoOnTwo' ? (
              <>{tournament.teamsCount} ком</>
            ) : (
              <>{tournament.participantsCount} чел</>
            )}
          </span>
        )}
      </div>
    </span>
  );
}
